/**
 * Canonical Public Profile URL helpers.
 *
 * There is ONE source of truth for the Public Profile route in the app:
 *
 *   /hub/predictor/<public_profile_id>
 *
 * This must exactly match the React Router path registered in `App.js`
 * (`/hub/predictor/:publicProfileId`) and the URL that the backend
 * returns from `GET /hub/my-predictor-profile` in its `profile_url_path`
 * field. Every entry point (MY PROFILE → OPEN PUBLIC PROFILE, MY
 * PROFILE → COPY LINK, Public Profile hero → COPY PROFILE LINK,
 * Leaderboard row link, Marketplace creator badge, etc.) MUST build its
 * URL through the helpers below so we can never regress into a
 * split-URL situation again.
 *
 * The helpers deliberately reject any non-canonical inputs (an empty
 * or non-string `public_profile_id`) rather than silently emitting
 * `/hub/predictor/undefined`.
 */

/** Route path used by React Router — DO NOT inline this literal anywhere else. */
export const PUBLIC_PROFILE_ROUTE_PATH = "/hub/predictor";

/**
 * Build the in-app route path (relative) for a given `public_profile_id`.
 * Returns `null` when the id is missing/invalid so callers can decide
 * whether to hide the link entirely.
 */
export function publicProfilePath(publicProfileId) {
  if (typeof publicProfileId !== "string" || publicProfileId.length === 0) {
    return null;
  }
  return `${PUBLIC_PROFILE_ROUTE_PATH}/${encodeURIComponent(publicProfileId)}`;
}

/**
 * Build the fully-qualified shareable URL (origin + canonical path)
 * for the given `public_profile_id`. Uses `window.location.origin`
 * so the copied link always matches the origin the user is on
 * (staging preview URL, production, etc.).
 */
export function publicProfileShareUrl(publicProfileId) {
  const path = publicProfilePath(publicProfileId);
  if (!path) return null;
  const origin = (typeof window !== "undefined" && window.location
                     && window.location.origin) || "";
  return `${origin}${path}`;
}
